import { useCallback, useEffect, useRef, useState } from "react";
import { BUILD_ID } from "@shared/build-id";
import { decideVersionAction } from "@shared/version";
import { isAppBusy } from "@/lib/app-busy";

/**
 * #769 — stale-bundle self-refresh.
 *
 * The running client knows its own build id (BUILD_ID, baked in at build time).
 * It polls the server's current build id from GET /api/health on two triggers:
 *
 *   - `visibilitychange`: fires both when a tab backgrounds and when it returns.
 *     A check that runs while the tab is hidden can silently reload it so the
 *     user comes back to a fresh page; a check on return finds versions already
 *     matched. This is the load-bearing path for the resumed iOS home-screen case.
 *   - a slow interval (~15 min): catches a foreground desktop tab left open
 *     across a deploy — which now toasts rather than reloads (see below).
 *
 * On a mismatch the pure `decideVersionAction` decides (Akira, #777):
 *   - backgrounded + idle → silent reload (nobody's looking; safe to swap)
 *   - foreground, OR busy (chat in flight / unsaved form) → toast instead, so
 *     we never reload out from under a user on the tab — including someone who
 *     filled a form hours ago, got called away, and reads as "idle" but has
 *     unsaved work in the field. Each check re-evaluates, so a toast persists
 *     until the user taps it or the tab backgrounds and self-reloads.
 */

const CHECK_INTERVAL_MS = 15 * 60 * 1000; // ~15 min
const HEALTH_URL = "/api/health";
// Guard against a reload loop during a rolling deploy (multiple server
// versions briefly behind a load balancer): auto-reload at most once per window.
const RELOAD_GUARD_KEY = "crosbe:last-auto-reload";
const RELOAD_GUARD_MS = 60 * 1000;

async function fetchServerVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    // Public route (rbac skip-listed). no-store so a proxy/SW never hands back
    // a cached id. credentials included to match the app's same-origin calls.
    const res = await fetch(HEALTH_URL, {
      cache: "no-store",
      credentials: "include",
      signal,
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: string | null };
    return body.version ?? null;
  } catch {
    // Offline / aborted / malformed — treat as "unknown", never act on it.
    return null;
  }
}

function canAutoReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? "0");
    return Date.now() - last > RELOAD_GUARD_MS;
  } catch {
    return true;
  }
}

function performReload(): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode edge) — reload anyway.
  }
  window.location.reload();
}

export interface VersionCheckState {
  /** A newer build is live but we deferred the reload (busy) — show the toast. */
  updateReady: boolean;
  /** User-tapped refresh from the toast. */
  refreshNow: () => void;
}

export interface VersionCheckOptions {
  /**
   * #4832: the page runs with nobody at it, so a new build reloads it even in
   * the foreground (see decideVersionAction). Read at each check, so changing
   * it neither re-runs the mount check nor restarts the interval.
   */
  unattended?: boolean;
  /**
   * #4836: the session behind the page has lapsed, so a new build toasts
   * instead of reloading into the sign-in page, unattended or not (#4931; see
   * decideVersionAction). Read at each check like `unattended`. When it
   * clears with an update waiting, the check runs again at once, so the page
   * picks up the new build as soon as its session is back.
   */
  sessionLapsed?: boolean;
}

export function useVersionCheck({ unattended = false, sessionLapsed = false }: VersionCheckOptions = {}): VersionCheckState {
  const [updateReady, setUpdateReady] = useState(false);
  const unattendedRef = useRef(unattended);
  const sessionLapsedRef = useRef(sessionLapsed);
  /** The mounted checks' abort signal, for the check a recovered session runs. */
  const signalRef = useRef<AbortSignal | undefined>(undefined);
  useEffect(() => {
    unattendedRef.current = unattended;
  }, [unattended]);

  const runCheck = useCallback(async (signal?: AbortSignal) => {
    const serverVersion = await fetchServerVersion(signal);
    const decide = () =>
      decideVersionAction({
        clientVersion: BUILD_ID,
        serverVersion,
        busy: isAppBusy(),
        // #777 (Akira): a foreground/visible tab always toasts; only a
        // backgrounded tab is silently auto-reloaded. document.hidden is true
        // only when the tab is truly backgrounded.
        foreground: !document.hidden,
        unattended: unattendedRef.current,
        sessionLapsed: sessionLapsedRef.current,
      });
    let action = decide();
    // #4836: a poll answered 401 in this same moment has stored its error,
    // but the page hears of it on the query cache's next tick (TanStack
    // notifies on setTimeout 0), so sessionLapsed is not set yet. An
    // unattended page waits that one tick and decides again before reloading.
    if (action === "reload" && unattendedRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (signal?.aborted) return;
      action = decide();
    }
    if (action === "reload") {
      if (canAutoReload()) performReload();
      else setUpdateReady(true); // guard tripped — offer manual refresh instead
    } else if (action === "toast") {
      setUpdateReady(true);
    }
    // "none": leave state as-is.
  }, []);

  useEffect(() => {
    const recovered = sessionLapsedRef.current && !sessionLapsed;
    sessionLapsedRef.current = sessionLapsed;
    if (recovered && updateReady) void runCheck(signalRef.current);
  }, [sessionLapsed, updateReady, runCheck]);

  useEffect(() => {
    const controller = new AbortController();
    signalRef.current = controller.signal;

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void runCheck(controller.signal);
      }
    };

    document.addEventListener("visibilitychange", onVisible);
    const interval = window.setInterval(() => void runCheck(controller.signal), CHECK_INTERVAL_MS);

    // One check shortly after mount catches a deploy that landed while this
    // session was loading (client already stale on first paint).
    void runCheck(controller.signal);

    return () => {
      controller.abort();
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
  }, [runCheck]);

  const refreshNow = useCallback(() => performReload(), []);

  return { updateReady, refreshNow };
}
